"use client";

import React from "react";
import { EyeOff } from "lucide-react";
import { useSession } from "next-auth/react";

type BlurGateProps = {
  unsafe?: boolean;
  className?: string;
  /** If provided, overrides default copy */
  message?: string;
  /** Optional: when true, disables click-through even if authenticated */
  locked?: boolean;
};

/**
 * BlurGate overlays media with a unified sensitive-content mask when either the content is marked
 * unsafe or the viewer is not authenticated. It does not manage navigation – parent decides what to do.
 */
export default function BlurGate({ unsafe, className = "", message, locked }: BlurGateProps) {
  const { status } = useSession();
  // Don't render gate while session is loading (prevents flash for logged-in users)
  if (status === "loading") return null;
  const authed = status === "authenticated";
  // Gate only for guests on unsafe content
  const shouldGate = Boolean(!authed && unsafe);
  if (!shouldGate) return null;

  const label = message ?? "Login to view";

  return (
    <div
      className={[
        "absolute inset-0 grid place-items-center overflow-hidden",
        locked ? "pointer-events-auto" : "pointer-events-none",
        className,
      ].join(" ")}
      aria-label={label}
    >
      {/* Soft radial vignette + backdrop blur */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.45),rgba(0,0,0,0.6))] backdrop-blur-lg" />
      <div className="relative z-10 flex items-center gap-2 rounded-full bg-black/60 text-white px-3 py-1.5 shadow-md">
        <EyeOff className="icon-20" aria-hidden="true" />
        <span className="text-sm leading-none">{label}</span>
      </div>
    </div>
  );
}
